import { cn } from "@/lib/utils"

import type { ReportItem } from "../../config/reports-data"
import { useReportUnlock } from "../../hooks/use-report-unlock"
import { useTabReportTimerLabel } from "../../hooks/use-tab-report-timer-label"

/**
 * Success sheet after a report purchase (Figma "Report Is Almost Ready"):
 * huge cover + CONNECT/ALIGN/INTERPRET bars. Same store (`sheetReport`) for
 * every purchasable report.
 */
export function ReportInProgressSheet({
  report,
  onDismiss,
  closing = false,
}: {
  report: ReportItem
  onDismiss: () => void
  closing?: boolean
}) {
  const purchaseUnlock = useReportUnlock(report.id)
  const tabTimerLabel = useTabReportTimerLabel(report.id)
  const remainingLabel = tabTimerLabel ?? purchaseUnlock.remainingLabel

  return (
    <div
      data-slot="report-in-progress-sheet"
      className={cn(
        "relative flex w-full flex-col items-center gap-6 overflow-hidden rounded-t-[24px] bg-dark-surface px-4 pb-4 pt-8",
        "shadow-[0_0_16px_rgba(255,83,127,0.6),0_0_32px_#9565fd]",
        closing ? "astro-sheet-down" : "astro-sheet-up",
      )}
    >
      <div className="flex w-full flex-col items-center gap-4">
        <img
          src={report.hugeCover}
          alt=""
          className="h-[200px] w-[350px] object-contain drop-shadow-[0_30px_20px_rgba(13,8,28,0.8)]"
        />
        <div className="relative z-10 flex w-full flex-col items-center gap-2 text-center">
          <h2 className="font-display text-2xl font-medium text-purple-light">
            Report Is Almost Ready
          </h2>
          <p className="font-body text-base leading-snug text-purple-light/70">
            We’re finishing your reading. While it unlocks, explore more insights or chat with an
            astrologer.
          </p>
        </div>
        <div className="flex w-full flex-col items-center gap-3 rounded-xl bg-purple-light/10 px-4 pb-2 pt-3">
          <div className="flex items-center justify-center gap-1.5">
            <span className="font-body text-sm font-semibold text-purple-light">
              Your reading unlocks in
            </span>
            <span className="flex h-5 items-center justify-center rounded-full bg-green/20 px-2 font-body text-xs font-semibold text-green">
              {remainingLabel}
            </span>
          </div>
          <div className="flex w-full gap-3">
            <ProgressStage label="Connect" />
            <ProgressStage label="Align" />
            <ProgressStage label="Interpret" current />
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
      >
        Got it
      </button>
    </div>
  )
}

function ProgressStage({
  label,
  current = false,
}: {
  label: string
  current?: boolean
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
      <div
        className={cn(
          "h-1.5 w-full overflow-hidden rounded-[3px]",
          current ? "bg-main-purple/40" : "bg-main-purple",
        )}
      >
        {current ? (
          <div className="report-step-progress h-1.5 w-full rounded-[3px] bg-main-purple" />
        ) : null}
      </div>
      <p className="w-full text-center font-body text-[10px] font-medium uppercase text-purple-light/70">
        {label}
      </p>
    </div>
  )
}
